import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../Utils/Axios";

function BlogDetails() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/admin/blogs/${id}`);
      setBlog(response.data.blog);
    } catch (error) {
      console.error("Fetch blog error:", error);
      setBlog(null);
    } finally {
      setIsLoading(false);
    }
  };

  const isUrdu = companyLanguage === 'ur';

  // Fallback dummy posts if API fails
  const dummyPosts = {
    1: {
      title: "Innovative Solutions for every Business Success.",
      date: "28 Feb",
      author: "Ellinien Loma",
      category: "Business",
      content: `
        <p>In today's rapidly evolving business landscape, innovation is not just a buzzword—it's a necessity. Companies that fail to adapt and innovate risk being left behind by more agile competitors.</p>
        
        <h2>The Importance of Innovation</h2>
        <p>Innovation drives growth, improves efficiency, and creates competitive advantages. It's about finding new ways to solve problems, meet customer needs, and create value.</p>
        
        <h2>Key Strategies for Innovation</h2>
        <ul>
          <li>Foster a culture of creativity and experimentation</li>
          <li>Invest in research and development</li>
          <li>Embrace new technologies and methodologies</li>
          <li>Listen to customer feedback and market trends</li>
          <li>Encourage collaboration across teams</li>
        </ul>
        
        <h2>Measuring Success</h2>
        <p>Successful innovation requires clear metrics and KPIs. Track your progress, learn from failures, and continuously refine your approach.</p>
      `
    },
    2: {
      title: "Harnessing Digital Transform a Roadmap Businesses.",
      date: "28 Feb",
      author: "Ellinien Loma",
      category: "Business",
      content: `
        <p>Digital transformation is reshaping industries and creating new opportunities for businesses willing to embrace change.</p>
        
        <h2>Understanding Digital Transformation</h2>
        <p>Digital transformation goes beyond simply adopting new technologies. It's about fundamentally rethinking how your business operates and delivers value to customers.</p>
        
        <h2>The Roadmap</h2>
        <ol>
          <li>Assess your current state and identify gaps</li>
          <li>Define your vision and objectives</li>
          <li>Develop a strategic plan</li>
          <li>Implement changes incrementally</li>
          <li>Monitor progress and adjust as needed</li>
        </ol>
        
        <h2>Common Challenges</h2>
        <p>Resistance to change, lack of expertise, and budget constraints are common challenges. Address these proactively to ensure success.</p>
      `
    }
  };

  if (isLoading) {
    return (
      <>
        <Helmet><title>Loading... - Blog</title></Helmet>
        <div className={`min-h-screen bg-white flex items-center justify-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} dir={isUrdu ? 'rtl' : 'ltr'}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'بلاگ لوڈ ہو رہا ہے...' : 'Loading blog...'}
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <Helmet><title>Blog Not Found</title></Helmet>
        <div className={`min-h-screen bg-white flex items-center justify-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} dir={isUrdu ? 'rtl' : 'ltr'}>
          <div className="text-center">
            <h1 className={`text-3xl font-bold text-gray-900 mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'بلاگ نہیں ملا' : 'Blog Not Found'}
            </h1>
            <Link to="/blog" className={`text-yellow-600 hover:text-yellow-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? '← بلاگ پر واپس' : '← Back to Blog'}
            </Link>
          </div>
        </div>
      </>
    );
  }

  const displayTitle = isUrdu && blog.titleUrdu ? blog.titleUrdu : blog.title;
  const displayContent = isUrdu && blog.contentUrdu ? blog.contentUrdu : blog.content;
  const displayAuthor = isUrdu && blog.authorUrdu ? blog.authorUrdu : blog.author;
  const displayCategory = isUrdu && blog.categoryUrdu ? blog.categoryUrdu : blog.category;

  return (
    <>
      <Helmet><title>{displayTitle} - Blog</title></Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        </div>
        <div className={`container mx-auto px-4 relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <Link to="/blog" className={`text-gray-800 hover:text-gray-900 mb-4 inline-flex items-center gap-2 font-semibold transition ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isUrdu ? "M10 19l-7-7m0 0l7-7m-7 7h18" : "M10 19l-7-7m0 0l7-7m-7 7h18"} />
            </svg>
            {companyLanguage === 'ur' ? 'بلاگ پر واپس' : 'Back to Blog'}
          </Link>
          <div className="flex items-center text-sm mb-4 flex-wrap gap-3">
            {blog.publishedDate && (
              <span className={`flex items-center gap-2 bg-white/80 px-3 py-1 rounded-full ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {new Date(blog.publishedDate).toLocaleDateString()}
              </span>
            )}
            {blog.author && (
              <span className={`flex items-center gap-2 bg-white/80 px-3 py-1 rounded-full ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {displayAuthor}
              </span>
            )}
            {blog.category && (
              <span className={`bg-yellow-500 text-gray-900 px-3 py-1 rounded-full font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {displayCategory}
              </span>
            )}
          </div>
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{displayTitle}</h1>
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-20 bg-white ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {blog.image && (
              <img src={blog.image} alt={displayTitle} className="w-full h-96 object-cover rounded-lg mb-8" />
            )}
            <div 
              className={`prose prose-lg max-w-none ${isUrdu ? 'font-urdu' : ''}`}
              dangerouslySetInnerHTML={{ __html: displayContent }}
              style={{
                lineHeight: '1.8',
                color: '#374151',
                fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : 'inherit'
              }}
            />
          </div>
        </div>
      </section>
      </div>
    </>
  );
}

export default BlogDetails;

